import { toDateFromServer } from '../api/dates';
import type { MealPackage } from '../types';

export type PaidPackageRow = {
    recipient: string;
    start: string;
    end: string;
};

export type PackageSnapshotLike = {
    child: (name: string) => { val: () => unknown };
};

function dateRangesOverlap(startA: string, endA: string, startB: string, endB: string): boolean {
    const a0 = toDateFromServer(startA).getTime();
    const a1 = toDateFromServer(endA).getTime();
    const b0 = toDateFromServer(startB).getTime();
    const b1 = toDateFromServer(endB).getTime();
    return a1 >= b0 && a0 <= b1;
}

/**
 * - Block if this recipient already has this exact catalog row (same start/end).
 * - If the catalog row has `allowsConcurrentPurchase === true`: may overlap other paid periods.
 * - Otherwise (strict row): block if there is **any** date-overlapping paid row for this recipient
 *   — even if that other catalog row has the flag. The flag only relaxes rules for **buying** that row, not for buying another strict row on top.
 */
export function isRecipientBlockedFromPurchasingCatalog(
    personId: string,
    catalog: Pick<MealPackage, 'start' | 'end' | 'allowsConcurrentPurchase'>,
    paidRows: PaidPackageRow[]
): boolean {
    const hasExact = paidRows.some((p) => p.recipient === personId && p.start === catalog.start && p.end === catalog.end);
    if (hasExact) return true;

    if (catalog.allowsConcurrentPurchase) {
        return false;
    }

    const hasBlockingOverlap = paidRows.some((p) => {
        if (p.recipient !== personId) return false;
        if (p.start === catalog.start && p.end === catalog.end) return false;
        return dateRangesOverlap(p.start, p.end, catalog.start, catalog.end);
    });

    return hasBlockingOverlap;
}
